import { useState, type ReactNode } from 'react';
import { Controller, useWatch, type UseFormReturn } from 'react-hook-form';
import {
  EMPLOYMENT_TYPE_LABELS,
  EMPLOYMENT_TYPES,
  IDEAL_CANDIDATE_PARTS,
  IDEAL_CANDIDATE_PROMPTS,
  SHIFT_DISPLAY_ZONES,
  WORK_MODE_LABELS,
  WORK_MODES,
  hasIdealCandidate,
  plan30Progress,
  plan90Progress,
  requestBriefTitle,
  type EmploymentType,
  type RequestEditMarks,
  type WorkMode,
} from '@nest-recruit/shared';
import { Checkbox } from '../../../components/Checkbox';
import { DatePicker } from '../../../components/DatePicker';
import { Field } from '../../../components/Field';
import { Input, Textarea } from '../../../components/Input';
import { MoreOptions } from '../../../components/MoreOptions';
import { PersonPicker, type PersonOption } from '../../../components/PersonPicker';
import { Segmented } from '../../../components/Segmented';
import { Select, type SelectOption } from '../../../components/Select';
import { timeOption } from '../../../components/TimePicker';
import { IdealProfileSteps } from '../../hiring-templates/components/IdealProfileSteps';
import { Plan30Form, Plan90Form } from '../../hiring-templates/components/TemplateForms';
import { EarlierText, WrittenBefore } from '../../hiring-templates/components/TemplateSectionsView';
import { useToolNames } from '../../tool-names/api/tool-names-api';
import type { FormValues } from '../requisition-form-values';
import { EditedMark } from './EditedMark';
import styles from './RequestJobSections.module.css';

/**
 * ADR 0075 (workflow review B1; owner 2026-11-01 Q1 a): the parts of Request a hire the requester writes for the job — the role
 * and the brief, the ideal candidate, and the job's Basics except the recruiter. Each is one tab of the request's card (owner,
 * 2026-11-01, "Request a hire as tabs"), so the long form reads as a few short steps; the job details people rarely touch are
 * folded under "More job details". Every part shows its "Edited by HR" mark when someone other than the requester changed it.
 *
 * ADR 0083: the Ideal candidate tab is the Ideal candidate profile template, and the 30 and 90-day plans are templates on tabs
 * of their own. What a request written before the templates says is kept and shown read-only above them.
 */

const RESPONSIBILITIES_MAX = 20000;
const ADVERT_MAX = 20000;
const BULLET_HINT = 'One point per line.';

/** ADR 0083: the 30 and 90-day points moved to their own template tabs; the role keeps these two. */
const ROLE_BRIEF_KEYS = ['why', 'achieve'] as const;

const BRIEF_PLACEHOLDERS: Readonly<Record<(typeof ROLE_BRIEF_KEYS)[number], string>> = {
  why: 'The team is short of one developer\nTwo client projects start in January',
  achieve: 'Ship the new booking flow by March',
};

/** Half-hour steps, as the job's Basics offers them. */
const SHIFT_TIMES: readonly SelectOption[] = Array.from({ length: 48 }, (_, index) => {
  const hours = String(Math.floor(index / 2)).padStart(2, '0');
  return timeOption(`${hours}:${index % 2 === 0 ? '00' : '30'}`);
});

interface SectionProps {
  readonly form: UseFormReturn<FormValues>;
  readonly marks: RequestEditMarks;
}

/** The role itself, why, and what they will achieve. Responsibilities and "why we need this role" are needed to send. */
export function RolePanel({ form, marks }: SectionProps): ReactNode {
  const errors = form.formState.errors;
  return (
    <div className={styles.fields}>
      <Field label="Responsibilities" isRequired hint="What this person will do, day to day. HR can't know this; you can." error={errors.responsibilities?.message} action={<EditedMark mark={marks.responsibilities} />}>
        {({ inputId, describedBy, isInvalid }) => (
          <Textarea
            id={inputId}
            aria-describedby={describedBy}
            rows={5}
            isInvalid={isInvalid}
            maxLength={RESPONSIBILITIES_MAX}
            placeholder="Build and look after the booking API. Review pull requests. Talk to the client's team each week."
            {...form.register('responsibilities', { onChange: () => form.clearErrors('responsibilities') })}
          />
        )}
      </Field>
      {ROLE_BRIEF_KEYS.map((key) => (
        <Field
          key={key}
          label={requestBriefTitle(key)}
          isRequired={key === 'why'}
          isOptional={key !== 'why'}
          hint={BULLET_HINT}
          error={key === 'why' ? errors.brief?.why?.message : undefined}
          action={<EditedMark mark={marks[key]} />}
        >
          {({ inputId, describedBy, isInvalid }) => (
            <Textarea
              id={inputId}
              aria-describedby={describedBy}
              rows={key === 'why' ? 3 : 2}
              isInvalid={isInvalid}
              placeholder={BRIEF_PLACEHOLDERS[key]}
              {...form.register(`brief.${key}`, { onChange: () => form.clearErrors('brief.why') })}
            />
          )}
        </Field>
      ))}
    </div>
  );
}

/**
 * ADR 0083 (owner 2026-11-01 Q4 a): the Ideal candidate profile template, eight boxes plus the Leadership add-on's Part 1 on a
 * role that manages people. It feeds Jev's ideal candidate profile in the job. A request written before the template shows its
 * four-part text read-only above it. Evening review 2026-11-01 item 6: one box at a time (`IdealProfileSteps`, shared with the
 * job by ADR 0092).
 */
export function IdealCandidatePanel({ form }: Pick<SectionProps, 'form'>): ReactNode {
  const [note, managesPeople] = useWatch({ control: form.control, name: ['idealCandidate', 'managesPeople'] });
  const toolNames = useToolNames();
  return (
    <div className={styles.fields}>
      {hasIdealCandidate(note) && (
        <WrittenBefore title="Written before the template">
          {IDEAL_CANDIDATE_PARTS.filter((part) => note[part].trim() !== '').map((part) => (
            <EarlierText key={part} text={`${IDEAL_CANDIDATE_PROMPTS[part].label}: ${note[part]}`} />
          ))}
        </WrittenBefore>
      )}
      <Controller
        control={form.control}
        name="idealProfile"
        render={({ field }) => <IdealProfileSteps value={field.value} managesPeople={managesPeople} toolNames={toolNames} onChange={(value) => field.onChange(value)} />}
      />
    </div>
  );
}

type PlanKey = 'plan30' | 'plan90';

const PLAN_CHOICES: Readonly<Record<PlanKey, string>> = { plan30: 'First 30 days', plan90: 'First 90 days' };

/**
 * ADR 0083 (Q5 a), evening review 2026-11-01 item 1: the 30 and 90-day plans as one "Onboarding plan" tab, optional to send.
 * "30-day plan" on its own read like a 30-day hiring timeline, so the tab says first that both cover the new hire's first days
 * after joining. A two-way switch shows one plan at a time; each says "Done" once its template is complete. The line manager
 * finishes them for each hire before Day 1.
 */
export function OnboardingPanel({ form }: Pick<SectionProps, 'form'>): ReactNode {
  const [plan, setPlan] = useState<PlanKey>('plan30');
  const [managesPeople, brief, plan30, plan90] = useWatch({ control: form.control, name: ['managesPeople', 'brief', 'plan30', 'plan90'] });
  const isDone: Readonly<Record<PlanKey, boolean>> = {
    plan30: plan30Progress(plan30, managesPeople).isComplete,
    plan90: plan90Progress(plan90, managesPeople).isComplete,
  };
  const earlier = (plan === 'plan30' ? brief.plan_30 : brief.plan_90).trim();
  return (
    <div className={styles.fields}>
      <p className={styles.lead}>
        This is about the new hire&rsquo;s <b>first 30 and 90 days after they join</b>: what they can do by Day 30, and what they own by Day 90
        (the probation test).
      </p>
      <Segmented
        label="Which part of the onboarding plan"
        value={plan}
        options={(Object.keys(PLAN_CHOICES) as PlanKey[]).map((key) => ({ value: key, label: isDone[key] ? `${PLAN_CHOICES[key]} · Done` : PLAN_CHOICES[key] }))}
        onChange={setPlan}
      />
      {earlier !== '' && (
        <WrittenBefore title={`${requestBriefTitle(plan === 'plan30' ? 'plan_30' : 'plan_90')}, written before the template`}>
          <EarlierText text={earlier} />
        </WrittenBefore>
      )}
      {plan === 'plan30' ? (
        <Controller control={form.control} name="plan30" render={({ field }) => <Plan30Form value={field.value} managesPeople={managesPeople} onChange={(next) => field.onChange(next)} />} />
      ) : (
        <Controller control={form.control} name="plan90" render={({ field }) => <Plan90Form value={field.value} managesPeople={managesPeople} onChange={(next) => field.onChange(next)} />} />
      )}
    </div>
  );
}

interface JobDetailsProps extends SectionProps {
  readonly people: readonly PersonOption[];
  /** What the job gets when nothing is chosen here: the seat's own facts, else the job's defaults. */
  readonly fallback: { readonly workMode: WorkMode; readonly employmentType: EmploymentType; readonly location: string | null; readonly title: string };
}

/** The job's Basics, all but the recruiter (HR picks that). The common few are open; the rest fold. */
export function JobDetailsPanel({ form, marks, people, fallback }: JobDetailsProps): ReactNode {
  const errors = form.formState.errors;
  const [shiftStart, ...folded] = useWatch({
    control: form.control,
    name: ['shiftStart', 'hiringManagerUserId', 'publicTitle', 'roleFamily', 'targetCloseDate', 'shiftHours', 'isConfidential'],
  });
  const foldedParts = ['hiringManager', 'publicTitle', 'roleFamily', 'targetCloseDate', 'shift', 'isConfidential'] as const;
  const isFoldOpen = shiftStart !== null || folded.some((value) => value !== null && value !== '' && value !== false) || foldedParts.some((part) => marks[part] !== undefined);
  return (
    <div className={styles.fields}>
      <Field label="Line manager" hint="Who the new hire reports to. Gets the shortlist." error={errors.lineManagerUserId?.message} action={<EditedMark mark={marks.lineManager} />}>
        {({ inputId, describedBy, isInvalid }) => (
          <Controller
            control={form.control}
            name="lineManagerUserId"
            render={({ field }) => <PersonPicker id={inputId} describedBy={describedBy} label="Line manager" isInvalid={isInvalid} people={people} value={field.value} onChange={field.onChange} />}
          />
        )}
      </Field>
      <div className={styles.row}>
        <Field label="Work mode" action={<EditedMark mark={marks.workMode} />}>
          {() => (
            <Controller
              control={form.control}
              name="workMode"
              render={({ field }) => (
                <Segmented label="Work mode" value={field.value ?? fallback.workMode} options={WORK_MODES.map((mode) => ({ value: mode, label: WORK_MODE_LABELS[mode] }))} onChange={field.onChange} />
              )}
            />
          )}
        </Field>
        <Field label="Employment type" action={<EditedMark mark={marks.employmentType} />}>
          {() => (
            <Controller
              control={form.control}
              name="employmentType"
              render={({ field }) => (
                <Segmented
                  label="Employment type"
                  value={field.value ?? fallback.employmentType}
                  options={EMPLOYMENT_TYPES.map((type) => ({ value: type, label: EMPLOYMENT_TYPE_LABELS[type] }))}
                  onChange={field.onChange}
                />
              )}
            />
          )}
        </Field>
      </div>
      <Field label="Location" isOptional hint={fallback.location ? `Leave it empty to use the position's: ${fallback.location}.` : undefined} action={<EditedMark mark={marks.location} />}>
        {({ inputId, describedBy }) => <Input id={inputId} aria-describedby={describedBy} maxLength={160} placeholder={fallback.location ?? 'Tejgaon, Dhaka'} {...form.register('location')} />}
      </Field>
      <Field label="About the job, for the advert" isOptional hint="What candidates read on the career site and job boards." action={<EditedMark mark={marks.description} />}>
        {({ inputId, describedBy }) => <Textarea id={inputId} aria-describedby={describedBy} rows={3} maxLength={ADVERT_MAX} placeholder="What the role is and who it suits" {...form.register('description')} />}
      </Field>
      <Field label="Requirements, for the advert" isOptional hint={BULLET_HINT} action={<EditedMark mark={marks.requirements} />}>
        {({ inputId, describedBy }) => <Textarea id={inputId} aria-describedby={describedBy} rows={3} maxLength={ADVERT_MAX} placeholder={'Two years of Laravel\nGood written English'} {...form.register('requirements')} />}
      </Field>

      <MoreOptions label="More job details" isInitiallyOpen={isFoldOpen}>
        <div className={styles.fields}>
          <Field label="Hiring manager" isOptional hint="Gets the manager's scorecards and decisions. Follows the line manager unless you choose someone." action={<EditedMark mark={marks.hiringManager} />}>
            {({ inputId, describedBy }) => (
              <Controller
                control={form.control}
                name="hiringManagerUserId"
                render={({ field }) => (
                  <PersonPicker id={inputId} describedBy={describedBy} label="Hiring manager" placeholder="Same as the line manager" isClearable people={people} value={field.value} onChange={field.onChange} />
                )}
              />
            )}
          </Field>
          <div className={styles.row}>
            <Field label="Title candidates see" isOptional action={<EditedMark mark={marks.publicTitle} />}>
              {({ inputId }) => <Input id={inputId} maxLength={160} placeholder={fallback.title || 'Often different from the internal title'} {...form.register('publicTitle')} />}
            </Field>
            <Field label="Role family" isOptional hint="Picks the pipeline HR set for it." action={<EditedMark mark={marks.roleFamily} />}>
              {({ inputId, describedBy }) => <Input id={inputId} aria-describedby={describedBy} maxLength={80} placeholder="Engineering" {...form.register('roleFamily')} />}
            </Field>
          </div>
          <Field label="Target close date" isOptional hint="When the advert should stop taking applications." action={<EditedMark mark={marks.targetCloseDate} />}>
            {({ inputId }) => (
              <Controller
                control={form.control}
                name="targetCloseDate"
                render={({ field }) => <DatePicker id={inputId} label="Target close date" placeholder="Choose a date" value={field.value} onChange={field.onChange} />}
              />
            )}
          </Field>
          <Field label="Shift" isOptional hint="In BD time, with the client's time zone if it differs." error={errors.shiftEnd?.message} action={<EditedMark mark={marks.shift} />}>
            {() => (
              <div className={styles.shift}>
                <Controller
                  control={form.control}
                  name="shiftStart"
                  render={({ field }) => (
                    <Select
                      label="Shift start, BD time"
                      placeholder="No set shift"
                      value={field.value}
                      options={[{ value: '', label: 'No set shift' }, ...SHIFT_TIMES]}
                      onChange={(value) => {
                        field.onChange(value === '' ? null : value);
                        form.clearErrors('shiftEnd');
                      }}
                    />
                  )}
                />
                <Controller
                  control={form.control}
                  name="shiftEnd"
                  render={({ field }) => <Select label="Shift end, BD time" placeholder="Ends" disabled={shiftStart === null} isInvalid={Boolean(errors.shiftEnd)} value={field.value} options={SHIFT_TIMES} onChange={(value) => { field.onChange(value); form.clearErrors('shiftEnd'); }} />}
                />
                <Controller
                  control={form.control}
                  name="shiftDisplayZone"
                  render={({ field }) => (
                    <Select
                      label="Client time zone"
                      placeholder="Client time zone"
                      disabled={shiftStart === null}
                      value={field.value}
                      options={SHIFT_DISPLAY_ZONES.map((zone) => ({ value: zone.value, label: zone.label }))}
                      onChange={(value) => { field.onChange(value); form.clearErrors('shiftEnd'); }}
                    />
                  )}
                />
              </div>
            )}
          </Field>
          <Field label="Shift note for candidates" isOptional>
            {({ inputId }) => <Input id={inputId} maxLength={200} placeholder="Night shift, Sunday to Thursday" {...form.register('shiftHours')} />}
          </Field>
          <Field label="Confidential" action={<EditedMark mark={marks.isConfidential} />}>
            {() => (
              <Controller
                control={form.control}
                name="isConfidential"
                render={({ field }) => <Checkbox checked={field.value} label="Keep this job off the career site and out of other people's lists" onChange={field.onChange} />}
              />
            )}
          </Field>
        </div>
      </MoreOptions>
    </div>
  );
}
